import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ThumbsUp, DollarSign, ShieldAlert, X } from "lucide-react";
import { trackOutcome } from "@/lib/outcomeTracker";
import { trackFeedback } from "@/lib/behaviorTracking";

interface Props {
  query: string;
  intentKey?: string;
  market?: string;
  verification?: string;
  winnerLabel?: string;
}

const options = [
  { icon: ThumbsUp, label: "Yes, helpful", value: "helpful" },
  { icon: DollarSign, label: "Too expensive", value: "expensive" },
  { icon: ShieldAlert, label: "Too risky", value: "risky" },
  { icon: X, label: "Not what I wanted", value: "wrong" },
];

export default function FeedbackLoop({ query, intentKey, market, verification, winnerLabel }: Props) {
  const [submitted, setSubmitted] = useState(false);

  const handleFeedback = (value: string) => {
    trackOutcome({
      type: "click",
      query,
      category: "feedback",
      optionId: value,
      optionLabel: `feedback:${value}`,
    });
    trackFeedback(query, value, { intentKey, market, verification, winnerLabel });
    setSubmitted(true);
  };

  return (
    <div className="text-center mt-10 mb-4">
      <AnimatePresence mode="wait">
        {!submitted ? (
          <motion.div
            key="ask"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <p className="text-sm text-muted-foreground font-body mb-4">Was this helpful?</p>
            <div className="flex flex-wrap justify-center gap-2">
              {options.map((opt) => (
                <motion.button
                  key={opt.value}
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.97 }}
                  onClick={() => handleFeedback(opt.value)}
                  className="flex items-center gap-1.5 px-4 py-2 rounded-lg border border-border bg-secondary text-secondary-foreground text-xs font-body hover:border-primary/30 transition-colors"
                >
                  <opt.icon className="w-3 h-3" />
                  {opt.label}
                </motion.button>
              ))}
            </div>
          </motion.div>
        ) : (
          <motion.p
            key="thanks"
            initial={{ opacity: 0, y: 5 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-sm text-muted-foreground font-body"
          >
            Thanks — this helps us improve.
          </motion.p>
        )}
      </AnimatePresence>
    </div>
  );
}
